/**
 * Soft looping ambient music for Memories slideshow.
 * Uses a public WAV pad; starts muted until the user unmutes.
 */

export const SLIDESHOW_AMBIENT_SRC = '/memories/slideshow-ambient.wav';
export const SLIDESHOW_INTERVAL_MIN = 2;
export const SLIDESHOW_INTERVAL_MAX = 20;
export const SLIDESHOW_INTERVAL_DEFAULT = 3;

export const SLIDESHOW_VOLUME_DEFAULT = 0.42;
export const SLIDESHOW_VOLUME_MIN = 0;
export const SLIDESHOW_VOLUME_MAX = 1;

function clampVolume(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return SLIDESHOW_VOLUME_DEFAULT;
  return Math.max(SLIDESHOW_VOLUME_MIN, Math.min(SLIDESHOW_VOLUME_MAX, n));
}

export function createSlideshowAmbient({
  src = null,
  muted = true,
  volume = SLIDESHOW_VOLUME_DEFAULT,
} = {}) {
  const trackSrc = src || SLIDESHOW_AMBIENT_SRC;
  let audio = null;
  let started = false;
  let isMuted = Boolean(muted);
  let level = clampVolume(volume);

  const ensure = () => {
    if (audio || typeof Audio === 'undefined') return;
    audio = new Audio(trackSrc);
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = level;
    audio.muted = isMuted;
  };

  const applyPlayback = async () => {
    if (!audio) return;
    audio.volume = level;
    audio.muted = isMuted || level <= 0;
    if (isMuted || level <= 0) {
      try {
        if (typeof audio.pause === 'function') audio.pause();
      } catch {
        /* ignore jsdom / autoplay */
      }
      return;
    }
    try {
      if (audio.paused) await audio.play();
    } catch {
      /* Autoplay may still block until a later unmute gesture. */
    }
  };

  return {
    async start() {
      ensure();
      if (!audio) return;
      started = true;
      await applyPlayback();
    },
    stop() {
      if (!audio) return;
      try {
        if (typeof audio.pause === 'function') audio.pause();
      } catch {
        /* ignore */
      }
      try {
        audio.src = '';
      } catch {
        /* ignore */
      }
      audio = null;
      started = false;
    },
    async setMuted(next) {
      isMuted = Boolean(next);
      await applyPlayback();
    },
    async setVolume(next) {
      level = clampVolume(next);
      if (level > 0 && isMuted) {
        // Raising volume implies the user wants sound.
        isMuted = false;
      }
      await applyPlayback();
    },
    getVolume() {
      return level;
    },
    isMuted() {
      return isMuted || level <= 0;
    },
    isStarted() {
      return started;
    },
  };
}
